/**
 * CopyableId Component (REQ-AUTH-078)
 *
 * Shows a ZITADEL account id and offers to copy it.
 *
 * The portal is an internal admin surface, and the account id — the value a
 * token carries as `sub` — is what identifies a person across organizations.
 * It is also the input the external-access grant takes (D2: named by user id,
 * never by email), so it has to be readable here and copyable in one click
 * rather than selected by hand out of a monospace run of digits.
 *
 * @module components/CopyableId
 */

import { useCallback, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

export interface CopyableIdProps {
  /** The id to show and copy. */
  value: string;
  /** Distinguishes the copy control when several appear on one page. */
  testIdPrefix?: string;
}

/** How long the copied confirmation stays on screen. */
const COPIED_FEEDBACK_MS = 1500;

export function CopyableId({
  value,
  testIdPrefix = 'copy-user-id',
}: CopyableIdProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      // A clipboard the browser refuses (no permission, insecure context) is
      // not an error worth interrupting an admin over — the id is on screen and
      // can still be selected by hand.
    }
  }, [value]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontFamily: 'monospace',
          fontSize: '0.8125rem',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={value}
      >
        {value}
      </Typography>
      <Tooltip title={copied ? 'Copied' : 'Copy user id'}>
        <IconButton
          size="small"
          aria-label={`Copy user id ${value}`}
          onClick={handleCopy}
          data-testid={`${testIdPrefix}-${value}`}
        >
          {copied ? (
            <CheckIcon fontSize="inherit" />
          ) : (
            <ContentCopyIcon fontSize="inherit" />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}
